import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { saveAs } from 'file-saver';
import api from '../services/api';
import SideDrawer from '../components/SideDrawer';
import {
  ArrowLeftIcon,
  DocumentArrowDownIcon,
  ClockIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import PageHeader from '../components/ui/PageHeader';

// Status color mapping
const STATUS_COLORS = {
  STARTED: 'bg-slate-100 text-slate-800',
  SENT: 'bg-blue-100 text-blue-800',
  IN_PROGRESS: 'bg-amber-100 text-amber-800',
  COMPLETED: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-gray-100 text-gray-800',
  USER_ABORTED: 'bg-orange-100 text-orange-800',
  SETTLED: 'bg-emerald-100 text-emerald-800',
  EVICTED: 'bg-purple-100 text-purple-800',
  DEFAULT: 'bg-gray-100 text-gray-800'
};

// Convert status key to readable label
const formatStatusLabel = (status) => {
  return status
    .split('_')
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
};

const formatFileSize = (bytes) => {
  if (!bytes) return 'N/A';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

function SlicerJobDetail({ jobId: propId, embedded = false }) {
  const params = useParams();
  const navigate = useNavigate();
  const id = propId || params.id;
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    stats: true,
    objects: false,
    files: true,
    plates: false,
  });
  const [showLogDrawer, setShowLogDrawer] = useState(false);
  const [logContent, setLogContent] = useState(null);
  const [logLoading, setLogLoading] = useState(false);

  const fetchJobDetail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/auth/slicer-job/${id}`);
      setJob(response.data.data);
    } catch (error) {
      console.error('Error fetching job detail:', error);
      alert('Failed to load job details');
      if (!embedded) navigate('/slicer-jobs');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- embedded is static prop
  }, [id, navigate]);

  useEffect(() => {
    fetchJobDetail();
  }, [fetchJobDetail]);

  const handleDownloadJobFile = async (fileName) => {
    try {
      const url = `/auth/slicer-job/download/${job._id}/${fileName}`;
      const response = await api.get(url, {
        responseType: 'blob'
      });
      saveAs(response.data, fileName);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file');
    }
  };

  const handleViewLog = async () => {
    setShowLogDrawer(true);
    if (logContent !== null) return; // Already loaded

    try {
      setLogLoading(true);
      const url = `/auth/slicer-job/download/${job._id}/slicer.log`;
      const response = await api.get(url, {
        responseType: 'text'
      });
      setLogContent(response.data);
    } catch (error) {
      console.error('Error fetching slicer log:', error);
      setLogContent('Failed to load slicer log.');
    } finally {
      setLogLoading(false);
    }
  };

  const handleDownloadLog = () => {
    if (logContent) {
      const blob = new Blob([logContent], { type: 'text/plain' });
      saveAs(blob, 'slicer.log');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const parseJsonSafely = (jsonString) => {
    if (!jsonString) return null;
    try {
      return JSON.parse(jsonString);
    } catch {
      return null;
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const StatusIcon = ({ status }) => {
    switch (status) {
      case 'COMPLETED':
      case 'SETTLED':
        return <CheckCircleIcon className="h-6 w-6 text-green-600" />;
      case 'FAILED':
        return <XCircleIcon className="h-6 w-6 text-red-600" />;
      case 'IN_PROGRESS':
      case 'SENT':
        return <ClockIcon className="h-6 w-6 text-amber-600" />;
      default:
        return <DocumentTextIcon className="h-6 w-6 text-gray-600" />;
    }
  };

  if (loading) {
    return <div className="p-4">Loading...</div>;
  }

  if (!job) {
    return <div className="p-4">Job not found</div>;
  }

  const stats = parseJsonSafely(job.stats);
  const objects = parseJsonSafely(job.objects);
  const hasSlicerLog = job.jobFiles?.some(f => f.fileName === 'slicer.log');

  return (
    <div className={embedded ? 'p-4' : 'p-5 max-w-7xl mx-auto'}>
      {/* Header */}
      <div className="mb-4">
        {!embedded && (
          <button
            onClick={() => navigate('/slicer-jobs')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            Back to Jobs
          </button>
        )}

        <PageHeader
          title="Slicer Job Details"
          description={job.jobId}
          actions={
            <div className="flex items-center gap-3">
              <span className={`px-4 py-2 rounded-lg font-medium ${STATUS_COLORS[job.status] || STATUS_COLORS.DEFAULT}`}>
                {formatStatusLabel(job.status)}
              </span>
              {job.is3MF && (
                <span className="px-3 py-2 bg-purple-100 text-purple-800 rounded-lg font-medium">
                  3MF
                </span>
              )}
            </div>
          }
        />
      </div>

      {/* Main Content Grid */}
      <div className={`grid grid-cols-1 ${embedded ? '' : 'lg:grid-cols-3'} gap-4`}>
        {/* Left Column - Status & Metadata */}
        <div className="lg:col-span-1 space-y-4">
          {/* Job Status Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-3 mb-4">
              <StatusIcon status={job.status} />
              <h2 className="text-lg font-semibold">Job Status</h2>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-600">Current Status</label>
                <p className={`font-medium text-lg px-3 py-1 rounded inline-block ${STATUS_COLORS[job.status] || STATUS_COLORS.DEFAULT}`}>
                  {formatStatusLabel(job.status)}
                </p>
              </div>
              {job.execCode !== undefined && job.execCode !== null && (
                <div>
                  <label className="text-sm text-gray-600">Exit Code</label>
                  <p className={`font-medium ${job.execCode === 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {job.execCode}
                  </p>
                </div>
              )}
              {job.slicerOutputCode !== undefined && job.slicerOutputCode !== null && (
                <div>
                  <label className="text-sm text-gray-600">Slicer Output Code</label>
                  <p className={`font-medium ${job.slicerOutputCode === 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {job.slicerOutputCode}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* File Information */}
          {job.file && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
                <DocumentTextIcon className="h-5 w-5 text-gray-500" />
                File Information
              </h2>
              <div className="space-y-2">
                <div>
                  <label className="text-sm text-gray-600">Filename</label>
                  <p className="font-medium text-gray-900 break-all">{job.file.name}</p>
                </div>
                <div className="flex gap-4 text-sm">
                  {job.file.size && (
                    <div>
                      <label className="text-xs text-gray-500">Size</label>
                      <p className="font-medium">{formatFileSize(job.file.size)}</p>
                    </div>
                  )}
                  {job.file.mimeType && (
                    <div>
                      <label className="text-xs text-gray-500">Type</label>
                      <p className="font-medium">{job.file.mimeType}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Associated Quote */}
          {job.quote && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-base font-semibold mb-4">Associated Quote</h2>
              <div className="space-y-2">
                <div>
                  <label className="text-sm text-gray-600">Quote ID</label>
                  <p className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                    {typeof job.quote === 'object' ? job.quote._id : job.quote}
                  </p>
                </div>
                <button
                  onClick={() => navigate(`/slicer-quotes/${typeof job.quote === 'object' ? job.quote._id : job.quote}`)}
                  className="w-full mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  View Quote Details
                </button>
              </div>
            </div>
          )}

          {/* Slicing Settings */}
          {job.settings && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
                <Cog6ToothIcon className="h-5 w-5 text-gray-500" />
                Slicing Settings
              </h2>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {job.settings.infill !== undefined && (
                  <div>
                    <label className="text-xs text-gray-500">Infill</label>
                    <p className="font-medium">{job.settings.infill}%</p>
                  </div>
                )}
                {job.settings.walls !== undefined && (
                  <div>
                    <label className="text-xs text-gray-500">Walls</label>
                    <p className="font-medium">{job.settings.walls}</p>
                  </div>
                )}
                {job.settings.topLayers !== undefined && (
                  <div>
                    <label className="text-xs text-gray-500">Top Layers</label>
                    <p className="font-medium">{job.settings.topLayers}</p>
                  </div>
                )}
                {job.settings.bottomLayers !== undefined && (
                  <div>
                    <label className="text-xs text-gray-500">Bottom Layers</label>
                    <p className="font-medium">{job.settings.bottomLayers}</p>
                  </div>
                )}
                {job.settings.support !== undefined && (
                  <div>
                    <label className="text-xs text-gray-500">Support</label>
                    <p className={`font-medium ${job.settings.support ? 'text-green-600' : 'text-gray-600'}`}>
                      {job.settings.support ? 'Enabled' : 'Disabled'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Job Metadata */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h2 className="text-base font-semibold mb-4">Job Metadata</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Job ID</span>
                <span className="font-mono text-xs truncate max-w-[200px]" title={job.jobId}>
                  {job.jobId}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">MongoDB ID</span>
                <span className="font-mono text-xs">{job._id}</span>
              </div>
              {job.plateCount !== undefined && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Plate Count</span>
                  <span className="font-medium">{job.plateCount}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Created</span>
                <span className="font-medium">{formatDate(job.createdAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Updated</span>
                <span className="font-medium">{formatDate(job.updatedAt)}</span>
              </div>
              {job.lastResponseAt && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Response</span>
                  <span className="font-medium">{formatDate(job.lastResponseAt)}</span>
                </div>
              )}
              {job.machine && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Machine</span>
                  <span className="font-medium">{typeof job.machine === 'object' ? (job.machine.name || job.machine._id) : job.machine}</span>
                </div>
              )}
              {job.printer && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Printer</span>
                  <span className="font-medium">{typeof job.printer === 'object' ? (job.printer.name || job.printer._id) : job.printer}</span>
                </div>
              )}
              {job.assignedMachine && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Assigned Machine</span>
                  <span className="font-medium">{typeof job.assignedMachine === 'object' ? (job.assignedMachine.name || job.assignedMachine._id) : job.assignedMachine}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Technical Details */}
        <div className="lg:col-span-2 space-y-4">
          {/* Warnings & Errors */}
          {(job.warnings || job.lastResponse) && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-base font-semibold mb-4 flex items-center gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
                Messages & Warnings
              </h2>

              {job.lastResponse && (
                <div className="mb-4">
                  <label className="text-sm font-medium text-gray-700 block mb-1">Last Response</label>
                  <details className="mt-4">
                    <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                      View raw JSON
                    </summary>
                    <pre className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
                      {JSON.stringify(JSON.parse(job.lastResponse), null, 2)}
                    </pre>
                  </details>
                </div>
              )}

              {job.warnings && (
                <div>
                  <label className="text-sm font-medium text-yellow-700 block mb-1">Warnings</label>
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800 whitespace-pre-wrap">
                    {job.warnings}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Statistics */}
          {stats && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleSection('stats')}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <CubeIcon className="h-5 w-5 text-gray-500" />
                  Slicing Statistics
                </h2>
                {expandedSections.stats ? (
                  <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                )}
              </button>
              {expandedSections.stats && (
                <div className="px-4 pb-4 border-t border-gray-200 pt-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {stats.printTime !== undefined && (
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <label className="text-xs text-blue-600 font-medium">Print Time</label>
                        <p className="text-lg font-semibold text-blue-900">
                          {formatDuration(stats.printTime)}
                        </p>
                      </div>
                    )}
                    {stats.filamentUsed !== undefined && (
                      <div className="p-3 bg-green-50 rounded-lg">
                        <label className="text-xs text-green-600 font-medium">Filament Used</label>
                        <p className="text-lg font-semibold text-green-900">
                          {stats.filamentUsed}g
                        </p>
                      </div>
                    )}
                    {stats.totalWeight !== undefined && (
                      <div className="p-3 bg-purple-50 rounded-lg">
                        <label className="text-xs text-purple-600 font-medium">Total Weight</label>
                        <p className="text-lg font-semibold text-purple-900">
                          {stats.totalWeight}g
                        </p>
                      </div>
                    )}
                    {stats.layerCount !== undefined && (
                      <div className="p-3 bg-amber-50 rounded-lg">
                        <label className="text-xs text-amber-600 font-medium">Layer Count</label>
                        <p className="text-lg font-semibold text-amber-900">
                          {stats.layerCount}
                        </p>
                      </div>
                    )}
                    {stats.estimatedCost !== undefined && (
                      <div className="p-3 bg-emerald-50 rounded-lg">
                        <label className="text-xs text-emerald-600 font-medium">Est. Cost</label>
                        <p className="text-lg font-semibold text-emerald-900">
                          {stats.estimatedCost}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Raw stats JSON for debugging */}
                  <details className="mt-4">
                    <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                      View raw stats JSON
                    </summary>
                    <pre className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
                      {JSON.stringify(stats, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </div>
          )}

          {/* Plates */}
          {job.plates && job.plates.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleSection('plates')}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <CubeIcon className="h-5 w-5 text-gray-500" />
                  Plates ({job.plates.length})
                </h2>
                {expandedSections.plates ? (
                  <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                )}
              </button>
              {expandedSections.plates && (
                <div className="px-4 pb-4 border-t border-gray-200 pt-4 space-y-4">
                  {job.plates.map((plate, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-gray-900">Plate {plate.plateIndex ?? index}</h3>
                        <div className="flex gap-2">
                          {plate.colorCount > 0 && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                              {plate.colorCount} color{plate.colorCount > 1 ? 's' : ''}
                            </span>
                          )}
                          {plate.objectCount > 0 && (
                            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                              {plate.objectCount} object{plate.objectCount > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Bounding Box */}
                      {plate.bbox && (
                        <div className="mb-3">
                          <label className="text-xs text-gray-500 font-medium">Bounding Box</label>
                          <p className="text-sm font-mono">
                            {plate.bbox.width?.toFixed(1)} x {plate.bbox.depth?.toFixed(1)} x {plate.bbox.height?.toFixed(1)} mm
                          </p>
                        </div>
                      )}

                      <div className="grid grid-cols-3 gap-3 text-sm">
                        {/* Time */}
                        {plate.totalTimeSeconds > 0 && (
                          <div className="p-2 bg-blue-50 rounded">
                            <label className="text-xs text-blue-600">Time</label>
                            <p className="font-medium text-blue-900">{formatDuration(plate.totalTimeSeconds)}</p>
                            {(plate.modelTimeSeconds > 0 || plate.supportTimeSeconds > 0) && (
                              <div className="text-xs text-blue-600 mt-1">
                                {plate.modelTimeSeconds > 0 && <div>Model: {formatDuration(plate.modelTimeSeconds)}</div>}
                                {plate.supportTimeSeconds > 0 && <div>Support: {formatDuration(plate.supportTimeSeconds)}</div>}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Weight */}
                        {plate.totalWeightGrams > 0 && (
                          <div className="p-2 bg-green-50 rounded">
                            <label className="text-xs text-green-600">Weight</label>
                            <p className="font-medium text-green-900">{plate.totalWeightGrams.toFixed(1)}g</p>
                            {(plate.modelWeightGrams > 0 || plate.supportWeightGrams > 0) && (
                              <div className="text-xs text-green-600 mt-1">
                                {plate.modelWeightGrams > 0 && <div>Model: {plate.modelWeightGrams.toFixed(1)}g</div>}
                                {plate.supportWeightGrams > 0 && <div>Support: {plate.supportWeightGrams.toFixed(1)}g</div>}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Volume */}
                        {plate.totalVolumeMM3 > 0 && (
                          <div className="p-2 bg-purple-50 rounded">
                            <label className="text-xs text-purple-600">Volume</label>
                            <p className="font-medium text-purple-900">{(plate.totalVolumeMM3 / 1000).toFixed(1)} cm³</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Objects */}
          {objects && objects.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleSection('objects')}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <h2 className="text-lg font-semibold">
                  Sliced Objects ({objects.length})
                </h2>
                {expandedSections.objects ? (
                  <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                )}
              </button>
              {expandedSections.objects && (
                <div className="px-4 pb-4 border-t border-gray-200 pt-4">
                  <div className="space-y-3">
                    {objects.map((obj, index) => (
                      <div key={index} className="p-3 bg-gray-50 rounded-lg">
                        <div className="font-medium">{obj.name || `Object ${index + 1}`}</div>
                        {obj.volume && (
                          <div className="text-sm text-gray-600">Volume: {obj.volume}mm³</div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Raw objects JSON */}
                  <details className="mt-4">
                    <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                      View raw objects JSON
                    </summary>
                    <pre className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
                      {JSON.stringify(objects, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </div>
          )}

          {/* S3 Key */}
          {job.s3Key && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-base font-semibold mb-4">Storage Location</h2>
              <div>
                <label className="text-sm text-gray-600">S3 Key</label>
                <p className="font-mono text-sm bg-gray-100 px-3 py-2 rounded mt-1 break-all">
                  {job.s3Key}
                </p>
              </div>
            </div>
          )}

          {/* Job Files */}
          {job.jobFiles && job.jobFiles.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleSection('files')}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <DocumentArrowDownIcon className="h-5 w-5 text-gray-500" />
                  Job Files ({job.jobFiles.length})
                </h2>
                {expandedSections.files ? (
                  <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                )}
              </button>
              {expandedSections.files && (
                <div className="px-4 pb-4 border-t border-gray-200 pt-4">
                  {!job.quote && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                      Note: File downloads require an associated quote. This job has no quote linked.
                    </div>
                  )}
                  <div className="space-y-2">
                    {job.jobFiles.map((file, index) => {
                      const isSlicerLog = file.fileName === 'slicer.log';

                      if (isSlicerLog) {
                        return (
                          <button
                            key={index}
                            onClick={handleViewLog}
                            disabled={!job.quote}
                            className={`w-full flex items-center justify-between p-3 rounded transition-colors ${job.quote
                              ? 'bg-amber-50 hover:bg-amber-100 cursor-pointer'
                              : 'bg-gray-100 cursor-not-allowed opacity-60'
                              }`}
                          >
                            <div className="flex flex-col items-start">
                              <span className={`font-medium ${job.quote ? 'text-amber-900' : 'text-gray-600'}`}>
                                {file.fileName}
                              </span>
                              {file.s3Key && (
                                <span className="text-xs text-gray-500 mt-1 truncate max-w-[400px]">
                                  {file.s3Key}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <EyeIcon className={`h-5 w-5 ${job.quote ? 'text-amber-600' : 'text-gray-400'}`} />
                              <span className={`text-xs ${job.quote ? 'text-amber-700' : 'text-gray-400'}`}>View</span>
                            </div>
                          </button>
                        );
                      }

                      return (
                        <button
                          key={index}
                          onClick={() => handleDownloadJobFile(file.fileName)}
                          disabled={!job.quote}
                          className={`w-full flex items-center justify-between p-3 rounded transition-colors ${job.quote
                            ? 'bg-green-50 hover:bg-green-100 cursor-pointer'
                            : 'bg-gray-100 cursor-not-allowed opacity-60'
                            }`}
                        >
                          <div className="flex flex-col items-start">
                            <span className={`font-medium ${job.quote ? 'text-green-900' : 'text-gray-600'}`}>
                              {file.fileName}
                            </span>
                            {file.s3Key && (
                              <span className="text-xs text-gray-500 mt-1 truncate max-w-[400px]">
                                {file.s3Key}
                              </span>
                            )}
                          </div>
                          <DocumentArrowDownIcon className={`h-5 w-5 ${job.quote ? 'text-green-600' : 'text-gray-400'}`} />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Client Output Files */}
          {job.clientOutputFiles && job.clientOutputFiles.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-base font-semibold mb-4">Client Output Files</h2>
              <div className="space-y-2">
                {job.clientOutputFiles.map((file, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded flex items-center gap-2">
                    <DocumentTextIcon className="h-5 w-5 text-gray-500" />
                    <span className="font-medium">{file}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Output Files */}
          {job.outputFiles && Object.keys(job.outputFiles).length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-base font-semibold mb-4">Output Files Map</h2>
              <div className="space-y-3">
                {Object.entries(job.outputFiles).map(([key, value]) => (
                  <div key={key} className="p-3 bg-gray-50 rounded">
                    <label className="text-sm text-gray-600">{key}</label>
                    <p className="font-mono text-xs break-all mt-1">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Slicer Log Drawer */}
      {hasSlicerLog && (
        <SideDrawer
          open={showLogDrawer}
          onClose={() => setShowLogDrawer(false)}
          title="Slicer Log"
        >
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-gray-600">slicer.log</span>
              <button
                onClick={handleDownloadLog}
                disabled={!logContent || logLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Download
              </button>
            </div>
            {logLoading ? (
              <div className="text-center py-12 text-gray-500">Loading log content...</div>
            ) : (
              <pre className="p-4 bg-gray-900 text-gray-100 rounded-lg text-xs font-mono overflow-auto max-h-[calc(100vh-180px)] whitespace-pre-wrap break-words">
                {logContent || 'No log content available.'}
              </pre>
            )}
          </div>
        </SideDrawer>
      )}
    </div>
  );
}

export default SlicerJobDetail;
